<template>
  <div class="page-container">
    <div class="page-title-container d-flex justify-content-between align-items-center">
      <h1 class="page-title mb-0">Distritos de {{ titleCase(prov) }}</h1>
      <div class="d-flex align-items-center">
        <b-button variant="none" class="button button-light mr-2"
          :to="{ name: 'ubigeo-provincias', params: { dept } }">
          Volver
        </b-button>
        <button class="button button-primary" @click="openActivateAll">
          <i class="ri-checkbox-multiple-line mr-1"></i> Activar todos
        </button>
      </div>
    </div>

    <div class="table-container">
      <ubigeo-table-header @reload="loadData()" />

      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-3">UBIGEO</th>
              <th>Distrito</th>
              <th>Capital</th>
              <th>Altitud</th>
              <th>Estado</th>
              <th class="pr-md-4">Ciudad asignada</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in rows" :key="d.id">
              <td class="pl-3 text-muted">{{ d.ubigeo_inei }}</td>
              <td class="font-weight-bold">{{ titleCase(d.distrito) }}</td>
              <td>{{ d.capital || '—' }}</td>
              <td class="text-center">{{ d.altitud_m != null ? d.altitud_m + ' m' : '—' }}</td>
              <td class="text-center">
                <b-form-checkbox switch :checked="d.is_active" @change="(val) => onToggle(d, val)">
                  <span :class="d.is_active ? 'text-success' : 'text-muted'">
                    {{ d.is_active ? 'Activo' : 'Inactivo' }}
                  </span>
                </b-form-checkbox>
              </td>
              <td class="text-center pr-md-3">
                <template v-if="d.is_active">
                  {{ cityName(d.city_id) }}
                  <button class="link-button ml-1" @click="openActivate(d)" title="Cambiar ciudad">
                    <i class="ri-edit-line"></i>
                  </button>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="6" class="text-center text-muted py-4">Sin distritos.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal ciudad (activar distrito individual o activar-todos) -->
    <CityAssignModal v-model="cityModal.show" :title="cityModal.title" @confirm="onCityConfirm" />

    <!-- Warning de bookings activos al desactivar -->
    <b-modal v-model="warnModal.show" title="Desactivar distrito" hide-footer>
      <p>{{ warnModal.message }}</p>
      <div class="text-right">
        <b-button variant="none" class="button button-light mr-2" @click="cancelDeactivate">Cancelar</b-button>
        <b-button variant="none" class="button button-primary" @click="forceDeactivate">Desactivar igual</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert } from 'wize-admin'
import { UbigeoService, GeographyService } from '@/core/services'
import CityAssignModal from '../Components/CityAssignModal.vue'
import UbigeoTableHeader from '../Components/UbigeoTableHeader.vue'

export default {
  name: 'DistrictList',
  components: { CityAssignModal, UbigeoTableHeader },
  data() {
    return {
      rows: [],
      cities: [],
      // mode: 'district' (activar 1) | 'province' (activar todos)
      cityModal: { show: false, title: '', mode: 'district', district: null },
      warnModal: { show: false, message: '', district: null }
    }
  },
  computed: {
    dept() { return this.$route.params.dept },
    prov() { return this.$route.params.prov }
  },
  methods: {
    titleCase(s) {
      if (!s) return ''
      return String(s).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
    },
    cityName(id) {
      const c = this.cities.find((x) => x.id === id)
      return c ? c.name : (id != null ? `Ciudad #${id}` : '—')
    },
    // Switch: val=true → activar (pide ciudad); val=false → desactivar (con check de bookings).
    onToggle(d, val) {
      if (val) this.openActivate(d)
      else this.deactivate(d, false)
    },
    openActivate(d) {
      this.cityModal = {
        show: true, mode: 'district',
        title: `Activar ${this.titleCase(d.distrito)} — selecciona ciudad`, district: d
      }
    },
    openActivateAll() {
      this.cityModal = {
        show: true, mode: 'province',
        title: `Activar todos los distritos de ${this.titleCase(this.prov)}`, district: null
      }
    },
    async onCityConfirm(cityId) {
      if (this.cityModal.mode === 'province') {
        await this.run(() => UbigeoService.activateAll(this.prov, { city_id: cityId, departamento: this.dept }),
          'Distritos activados')
      } else {
        const d = this.cityModal.district
        await this.run(() => UbigeoService.toggle(d.id, { is_active: true, city_id: cityId }),
          'Distrito activado')
      }
    },
    async deactivate(d, force) {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await UbigeoService.toggle(d.id, { is_active: false, force })
        const p = res.payload || {}
        if (!p.applied) {
          // Backend advierte bookings activos → pedir confirmación y reintentar con force.
          this.warnModal = { show: true, message: p.warning || 'El distrito tiene bookings activos.', district: d }
          return
        }
        Alert.success('Distrito desactivado')
        await this.loadData()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo desactivar el distrito')
        await this.loadData()
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    forceDeactivate() {
      const d = this.warnModal.district
      this.warnModal.show = false
      if (d) this.deactivate(d, true)
    },
    cancelDeactivate() {
      this.warnModal.show = false
      this.loadData() // revierte el switch al estado real
    },
    async run(fn, okMsg) {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await fn()
        Alert.success(typeof res.payload === 'string' ? res.payload : okMsg)
        await this.loadData()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'La operación falló')
        await this.loadData()
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async loadCities() {
      try {
        const res = await GeographyService.listCities()
        this.cities = res.payload || []
      } catch (e) { console.error(e) }
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await UbigeoService.distritos(this.dept, this.prov)
        this.rows = res.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al obtener distritos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    }
  },
  async mounted() {
    await this.loadCities()
    await this.loadData()
  }
}
</script>

<style lang="stylus" scoped>
.link-button
  background none
  border none
  padding 0
  color #007bff
  cursor pointer
  &:hover
    color #0056b3
</style>
